import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { GRN, GRNStatus, CreateGRNInput } from '@/types/database/grn';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export const useGRNs = (filters?: { 
  status?: GRNStatus; 
  verification_status?: string;
  date_from?: string;
  date_to?: string;
}) => {
  return useQuery({
    queryKey: ['grns', filters],
    queryFn: async () => {
      let query = (supabase as any)
        .from('grn')
        .select(`
          *,
          procurement:procurements(id, product_name, total_quantity),
          supplier:suppliers(id, name),
          agent:agents(id, name)
        `)
        .order('created_at', { ascending: false });
      
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.verification_status) {
        query = query.eq('verification_status', filters.verification_status);
      }
      if (filters?.date_from) {
        query = query.gte('delivery_date', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('delivery_date', filters.date_to);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as unknown as GRN[];
    },
  });
};

export const useGRN = (id: string) => {
  return useQuery({
    queryKey: ['grn', id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('grn')
        .select(`
          *,
          procurement:procurements(id, product_name, total_quantity),
          supplier:suppliers(id, name),
          agent:agents(id, name),
          items:grn_items(*),
          documents:grn_documents(*)
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data as unknown as GRN;
    },
    enabled: !!id,
  });
};

export const useCreateGRN = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (input: CreateGRNInput) => {
      // Generate GRN number
      const { data: numberData, error: numberError } = await supabase
        .rpc('generate_grn_number');
      
      if (numberError) throw numberError;
      
      const { items, ...grnData } = input;
      
      // Create GRN
      const { data: grn, error: grnError } = await (supabase as any)
        .from('grn')
        .insert({
          ...grnData,
          grn_number: numberData,
          received_by: user?.id,
          status: 'draft',
        })
        .select()
        .single();
      
      if (grnError) throw grnError;
      
      // Create items if provided
      if (items && items.length > 0) {
        const itemsWithGrnId = items.map(item => ({
          ...item,
          grn_id: grn.id,
        }));
        
        const { error: itemsError } = await supabase
          .from('grn_items')
          .insert(itemsWithGrnId);
        
        if (itemsError) throw itemsError;
      }
      
      return grn as GRN;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      toast.success('GRN created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create GRN: ${error.message}`);
    },
  });
};

export const useUpdateGRNStatus = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ id, status, verification_status, notes }: { 
      id: string; 
      status?: GRNStatus;
      verification_status?: string;
      notes?: string;
    }) => {
      const updateData: Record<string, unknown> = {};
      
      if (status) {
        updateData.status = status;
        if (status === 'received') {
          updateData.received_at = new Date().toISOString();
          updateData.received_by = user?.id;
        }
      }
      
      if (verification_status) {
        updateData.verification_status = verification_status;
        updateData.verified_by = user?.id;
        updateData.verified_at = new Date().toISOString();
      }
      
      if (notes) {
        updateData.discrepancy_notes = notes;
      }
      
      const { data, error } = await (supabase as any)
        .from('grn')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as GRN;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      toast.success('GRN updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update GRN: ${error.message}`);
    },
  });
};

export const useGRNStats = () => {
  return useQuery({
    queryKey: ['grn-stats'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('grn')
        .select('status, verification_status');
      
      if (error) throw error;
      
      const draft = data.filter(g => g.status === 'draft').length;
      const received = data.filter(g => g.status === 'received').length;
      const verified = data.filter(g => g.status === 'verified').length;
      const pending_verification = data.filter(g => g.verification_status === 'pending').length;
      
      return {
        total: data.length,
        draft,
        received,
        verified,
        pending_verification,
      };
    },
  });
};
